import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  User,
  Phone,
  Mail,
  Calendar,
  Target,
  Award,
  IndianRupee,
  Clock,
  CheckCircle2,
  FileText,
  TrendingUp,
  Users,
  Loader2,
  Edit2,
  ExternalLink,
  MessageSquare,
  Presentation,
  Handshake,
  Sparkles
} from 'lucide-react';
import EditPlanAmountDialog from '../../components/clients/EditPlanAmountDialog';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { clientsAPI, leadsAPI, demosAPI, dealsAPI, incentivesAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { usePrivacy } from '../../contexts/PrivacyContext';
import { cn } from '../../lib/utils';
import PricingHistory from '../../components/deals/PricingHistory';

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const formatDateTime = (date) => {
  if (!date) return '-';
  // Ensure UTC timestamps are properly converted - append 'Z' if no timezone
  let dateStr = date;
  if (typeof date === 'string' && !date.endsWith('Z') && !date.includes('+')) {
    dateStr = date + 'Z';
  }
  return new Date(dateStr).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

// Timeline Item Component
const TimelineItem = ({ icon: Icon, title, subtitle, date, status, isLast, color = 'blue', details }) => (
  <div className="flex gap-4">
    <div className="flex flex-col items-center">
      <div className={cn(
        "w-12 h-12 rounded-full flex items-center justify-center shadow-sm",
        color === 'blue' && 'bg-blue-100 text-blue-600 dark:bg-blue-900/50 dark:text-blue-400',
        color === 'emerald' && 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/50 dark:text-emerald-400',
        color === 'amber' && 'bg-amber-100 text-amber-600 dark:bg-amber-900/50 dark:text-amber-400',
        color === 'purple' && 'bg-purple-100 text-purple-600 dark:bg-purple-900/50 dark:text-purple-400',
        color === 'green' && 'bg-green-100 text-green-600 dark:bg-green-900/50 dark:text-green-400',
        color === 'cyan' && 'bg-cyan-100 text-cyan-600 dark:bg-cyan-900/50 dark:text-cyan-400'
      )}>
        <Icon className="w-5 h-5" />
      </div>
      {!isLast && <div className="w-0.5 flex-1 bg-slate-200 dark:bg-slate-700 min-h-[60px]" />}
    </div>
    <div className="pb-8 flex-1">
      <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700 shadow-sm">
        <div className="flex items-start justify-between">
          <div>
            <p className="font-semibold text-slate-800 dark:text-slate-100">{title}</p>
            {subtitle && <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{subtitle}</p>}
          </div>
          {status && (
            <Badge variant="outline" className="text-xs">
              {status}
            </Badge>
          )}
        </div>
        <p className="text-xs text-slate-400 dark:text-slate-500 mt-2 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {date}
        </p>
        {details && (
          <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
            <div className="grid grid-cols-2 gap-2 text-sm">
              {Object.entries(details).map(([key, value]) => (
                <div key={key}>
                  <span className="text-slate-500 dark:text-slate-400">{key}:</span>
                  <span className="ml-1 font-medium text-slate-700 dark:text-slate-300">{value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
);

const ClientDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { formatMoney } = usePrivacy();
  const formatCurrency = formatMoney; // Alias for compatibility
  
  const isAdmin = currentUser?.role === 'ADMIN';
  const isManager = currentUser?.role === 'MANAGER' || currentUser?.role === 'SALES_MANAGER';
  
  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState(null);
  const [lead, setLead] = useState(null);
  const [demo, setDemo] = useState(null);
  const [deal, setDeal] = useState(null);
  const [incentives, setIncentives] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [realTimeline, setRealTimeline] = useState([]);

  useEffect(() => {
    fetchClientData();
  }, [id]);

  const fetchClientData = async () => {
    try {
      setLoading(true);
      
      // Get client (which is actually a deal)
      const clientRes = await clientsAPI.get(id);
      const clientData = clientRes.data;
      setClient(clientData);
      
      // Get the deal details
      if (clientData.id) {
        try {
          const dealRes = await dealsAPI.get(clientData.id);
          const dealData = dealRes.data;
          setDeal(dealData);
          
          // Get demo from deal
          if (dealData.demo_id) {
            try {
              const demoRes = await demosAPI.get(dealData.demo_id);
              setDemo(demoRes.data);
            } catch (e) {
              console.log('Could not fetch demo');
            }
          }
          
          // Get lead from deal
          if (dealData.lead_id) {
            try {
              const leadRes = await leadsAPI.get(dealData.lead_id);
              setLead(leadRes.data);
            } catch (e) {
              console.log('Could not fetch lead');
            }
          }
        } catch (e) {
          console.log('Could not fetch deal details');
        }
      }
      
      // Get incentives related to this deal
      try {
        const incRes = await incentivesAPI.listRecords({ deal_id: id });
        const incData = incRes.data;
        setIncentives(Array.isArray(incData) ? incData : (incData?.records || []));
      } catch (e) {
        setIncentives([]);
      }
      
      // Get real timeline from API
      try {
        const timelineRes = await clientsAPI.getTimeline(id);
        setRealTimeline(timelineRes.data?.timeline || []);
      } catch (e) {
        console.log('Could not fetch timeline');
        setRealTimeline([]);
      }
      
    } catch (error) {
      console.error('Failed to fetch client data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        <span className="ml-2 text-slate-500">Loading client details...</span>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Client not found</p>
        <Button variant="outline" onClick={() => navigate('/clients')} className="mt-4">
          Back to Clients
        </Button>
      </div>
    );
  }

  // Build timeline data
  const timelineItems = [];
  
  if (lead) {
    timelineItems.push({
      icon: FileText,
      title: 'Lead Created',
      subtitle: `New lead added to the pipeline`,
      date: formatDateTime(lead.created_at),
      color: 'blue',
      details: {
        'Company': lead.company_name,
        'Contact': lead.contact_name,
        'Source': lead.source_channel?.replace(/_/g, ' ') || 'Direct',
        'Status': 'NEW'
      }
    });

    // Add Lead Qualified entry if we have SQD/Demo and dates differ significantly, or if status explicitly changed
    if (lead.current_status === 'DEMO_QUALIFIED_SQD' && !demo) {
         timelineItems.push({
            icon: MessageSquare,
            title: 'Lead Qualified',
            subtitle: 'Lead qualified for demo',
            date: formatDateTime(lead.updated_at),
            color: 'cyan'
          });
    }
  }

  if (demo) {
    timelineItems.push({
      icon: Presentation,
      title: 'Demo Scheduled',
      subtitle: `Product demonstration arranged`,
      date: formatDateTime(demo.demo_datetime), // Use scheduled time
      color: 'amber',
      details: {
        'Duration': demo.duration_minutes || '60 mins',
        'Assigned To': demo.assigned_employee_id?.slice(-8) || 'Team'
      }
    });

    if (demo.completed_at) {
      timelineItems.push({
        icon: CheckCircle2,
        title: 'Demo Completed',
        subtitle: `Demo conducted successfully`,
        date: formatDateTime(demo.completed_at),
        color: 'emerald',
        details: {
          'Rating': demo.demo_rating ? `${demo.demo_rating}/10` : 'N/A',
          'Notes': demo.demo_notes?.slice(0, 50) || 'None'
        }
      });
    }

    if (demo.is_sqd && demo.sqd_approved_at) {
      timelineItems.push({
        icon: Target,
        title: 'Sales Qualified Demo (SQD)',
        subtitle: demo.sqd_notes || 'Demo marked as qualified by manager',
        date: formatDateTime(demo.sqd_approved_at),
        color: 'purple',
        status: 'QUALIFIED'
      });
    }
  }

  if (deal) {
    timelineItems.push({
      icon: Handshake,
      title: 'Deal Created',
      subtitle: `Sales negotiation initiated`,
      date: formatDateTime(deal.created_at),
      color: 'blue',
      details: {
        'Initial Value': formatCurrency(deal.deal_value_estimate),
        'Plan': deal.plan_name || 'Custom',
        'Users': deal.total_users || 'N/A'
      }
    });

    if (deal.actual_close_date && deal.deal_stage?.includes('WON')) {
      timelineItems.push({
        icon: Sparkles,
        title: 'Deal Won - Client Converted!',
        subtitle: `Successfully converted to paying customer`,
        date: formatDateTime(deal.actual_close_date),
        status: deal.deal_stage?.replace(/_/g, ' '),
        color: 'green',
        details: {
          'Final Value': formatCurrency(deal.total_amount || deal.deal_value_estimate),
          'MRR': formatCurrency(deal.mrr || client.mrr),
          'Users': deal.total_users || client.total_users
        }
      });
    }
  }
  
  // Sort timeline items by date
  timelineItems.sort((a, b) => new Date(a.date) - new Date(b.date));

  const getStatusColor = (status) => {
    if (status?.includes('LIVE') || status?.includes('ACTIVE')) return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400';
    if (status?.includes('WON')) return 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400';
    if (status?.includes('IMPLEMENTATION')) return 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400';
    return 'bg-slate-100 text-slate-700 dark:bg-slate-900/50 dark:text-slate-400';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/clients')}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">{client.company_name || 'Client'}</h1>
            <p className="text-slate-500 dark:text-slate-400">{client.contact_name || ''}</p>
            <div className="flex items-center gap-3 mt-1 text-xs">
              {deal?.lead_id && (
                <span 
                  className="text-blue-600 hover:underline cursor-pointer"
                  onClick={() => navigate(`/leads/${deal.lead_id}`)}
                >
                  Lead: {deal.lead_id.slice(-8)}
                </span>
              )}
              {deal?.id && (
                <span 
                  className="text-purple-600 hover:underline cursor-pointer"
                  onClick={() => navigate(`/deals/${deal.id}`)}
                >
                  Deal: {deal.id.slice(-8)}
                </span>
              )}
            </div>
          </div>
        </div>
        <Badge className={cn('px-3 py-1 text-sm', getStatusColor(client.status))}>
          {client.status?.replace(/_/g, ' ') || 'Active'}
        </Badge>
      </div>

      {/* Stats Cards - Row 1: Revenue */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/30 dark:to-emerald-800/30 border-emerald-200 dark:border-emerald-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Monthly Revenue</p>
                <p className="text-xl font-bold text-emerald-700 dark:text-emerald-300">{formatCurrency(client.mrr || client.plan_amount)}</p>
              </div>
              <IndianRupee className="w-8 h-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/30 border-purple-200 dark:border-purple-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-purple-600 dark:text-purple-400 font-medium">Total Users</p>
                <p className="text-xl font-bold text-purple-700 dark:text-purple-300">{client.total_users || 0}</p>
              </div>
              <Users className="w-8 h-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/30 dark:to-amber-800/30 border-amber-200 dark:border-amber-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">Close Date</p>
                <p className="text-xl font-bold text-amber-700 dark:text-amber-300">{formatDate(client.close_date)}</p>
              </div>
              <Calendar className="w-8 h-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>

        {/* Salesperson Card */}
        <Card className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900/30 dark:to-slate-800/30 border-slate-200 dark:border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-600 dark:text-slate-400 font-medium">Salesperson</p>
                <p className="text-lg font-bold text-slate-700 dark:text-slate-300">{client.salesperson_name || 'Unassigned'}</p>
                {client.salesperson_email && (
                  <p className="text-xs text-slate-500 truncate max-w-[150px]">{client.salesperson_email}</p>
                )}
              </div>
              <User className="w-8 h-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stats Cards - Row 2: Incentives */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Company Incentive Card - Admin only */}
        {isAdmin && (
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 border-blue-200 dark:border-blue-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-blue-600 dark:text-blue-400 font-semibold flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Company Incentive Earned
                </p>
                <TrendingUp className="w-6 h-6 text-blue-400" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-blue-500 dark:text-blue-400">Invoiced</p>
                  <p className="text-xl font-bold text-blue-700 dark:text-blue-300">{formatCurrency(client.company_incentive_invoiced || 0)}</p>
                </div>
                <div>
                  <p className="text-xs text-blue-500 dark:text-blue-400">Expected</p>
                  <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{formatCurrency(client.company_incentive_expected || 0)}</p>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-blue-200 dark:border-blue-700">
                <p className="text-xs text-blue-500">Total: {formatCurrency((client.company_incentive_invoiced || 0) + (client.company_incentive_expected || 0))}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Employee Incentive Card - Visible to Admin and the assigned employee */}
        <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100 dark:from-cyan-900/30 dark:to-cyan-800/30 border-cyan-200 dark:border-cyan-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-cyan-600 dark:text-cyan-400 font-semibold flex items-center gap-2">
                <Award className="w-4 h-4" />
                Employee Incentive Earned
              </p>
              <Award className="w-6 h-6 text-cyan-400" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-cyan-500 dark:text-cyan-400">Paid</p>
                <p className="text-xl font-bold text-cyan-700 dark:text-cyan-300">{formatCurrency(client.employee_incentive_paid || 0)}</p>
              </div>
              <div>
                <p className="text-xs text-cyan-500 dark:text-cyan-400">Pending</p>
                <p className="text-xl font-bold text-cyan-600 dark:text-cyan-400">{formatCurrency(client.employee_incentive_pending || 0)}</p>
              </div>
            </div>
            <div className="mt-2 pt-2 border-t border-cyan-200 dark:border-cyan-700">
              <p className="text-xs text-cyan-500">Total: {formatCurrency((client.employee_incentive_paid || 0) + (client.employee_incentive_pending || 0))}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="-mx-4 px-4 overflow-x-auto scrollbar-hide">
          <TabsList className="bg-slate-100 dark:bg-slate-800 min-w-max">

          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="timeline">Journey Timeline</TabsTrigger>
          <TabsTrigger value="incentives">Incentives ({incentives.length})</TabsTrigger>
          </TabsList>
        </div>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Client Info */}
            <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-500" />
                  Client Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Company</p>
                    <p className="font-medium text-slate-800 dark:text-slate-100">{client.company_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Contact</p>
                    <p className="font-medium text-slate-800 dark:text-slate-100">{client.contact_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Plan</p>
                    <p className="font-medium text-slate-800 dark:text-slate-100">{client.plan_name || deal?.plan_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Status</p>
                    <Badge variant="outline" className={getStatusColor(client.status)}>{client.status?.replace(/_/g, ' ')}</Badge>
                  </div>
                </div>
                
                {lead && (
                  <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">Contact Details</p>
                    <div className="space-y-2">
                      {lead.contact_phone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-4 h-4 text-slate-400" />
                          <span className="text-slate-700 dark:text-slate-300">{lead.contact_phone}</span>
                        </div>
                      )}
                      {lead.contact_email && (
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="w-4 h-4 text-slate-400" />
                          <span className="text-slate-700 dark:text-slate-300">{lead.contact_email}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Deal Info */}
            <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Target className="w-5 h-5 text-emerald-500" />
                  Deal Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Deal Value</p>
                    <p className="font-medium text-slate-800 dark:text-slate-100">{formatCurrency(deal?.total_amount || client.total_value)}</p>
                  </div>
                  <div>
                    <div className="flex items-center">
                        <p className="text-sm text-slate-500 dark:text-slate-400">Monthly Plan</p>
                        {(isAdmin || isManager) && (
                            <EditPlanAmountDialog 
                                client={client} 
                                onUpdate={(newAmount) => {
                                    setClient({...client, plan_amount: newAmount, mrr: newAmount});
                                    fetchClientData(); // Refresh to ensure sync
                                }} 
                            />
                        )}
                    </div>
                    <p className="font-medium text-slate-800 dark:text-slate-100">{formatCurrency(client.plan_amount || client.mrr)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Users</p>
                    <p className="font-medium text-slate-800 dark:text-slate-100">{client.total_users || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Close Date</p>
                    <p className="font-medium text-slate-800 dark:text-slate-100">{formatDate(client.close_date)}</p>
                  </div>
                </div>
                
                {/* Extra Users Bifurcation */}
                {deal && deal.total_users > 0 && deal.base_user_capacity > 0 && deal.total_users > deal.base_user_capacity && (
                  <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                    <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-2 flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      Extra Users Breakdown
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-amber-600 dark:text-amber-500">Base Users (Included):</span>
                        <span className="ml-1 font-medium text-slate-800 dark:text-slate-200">{deal.base_user_capacity}</span>
                      </div>
                      <div>
                        <span className="text-amber-600 dark:text-amber-500">Total Users:</span>
                        <span className="ml-1 font-medium text-slate-800 dark:text-slate-200">{deal.total_users}</span>
                      </div>
                      <div>
                        <span className="text-amber-600 dark:text-amber-500">Extra Users:</span>
                        <span className="ml-1 font-medium text-amber-800 dark:text-amber-300">{deal.total_users - deal.base_user_capacity}</span>
                      </div>
                      {deal.extra_user_rate > 0 && (
                        <div>
                          <span className="text-amber-600 dark:text-amber-500">Rate per Extra User:</span>
                          <span className="ml-1 font-medium text-slate-800 dark:text-slate-200">{formatCurrency(deal.extra_user_rate)}/mo</span>
                        </div>
                      )}
                    </div>
                    {deal.extra_user_rate > 0 && (
                      <div className="mt-2 pt-2 border-t border-amber-200 dark:border-amber-700">
                        <span className="text-xs text-amber-600 dark:text-amber-500">Extra Users Cost:</span>
                        <span className="ml-1 font-semibold text-amber-800 dark:text-amber-300">
                          {formatCurrency((deal.total_users - deal.base_user_capacity) * deal.extra_user_rate)}/mo
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Quick Actions */}
                <div className="pt-4 border-t border-slate-200 dark:border-slate-700 flex gap-2">
                  {lead && (
                    <Button variant="outline" size="sm" onClick={() => navigate(`/leads/${lead.id}`)}>
                      <FileText className="w-4 h-4 mr-1" /> View Lead
                    </Button>
                  )}
                  {deal && (
                    <Button variant="outline" size="sm" onClick={() => navigate(`/deals/${deal.id}`)}>
                      <Handshake className="w-4 h-4 mr-1" /> View Deal
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Pricing History - NEW */}
            {deal?.pricing_revision_history && (
                <div className="lg:col-span-2">
                    <PricingHistory historyJson={deal.pricing_revision_history} />
                </div>
            )}
          </div>
        </TabsContent>

        {/* Timeline Tab - Real Data from API */}
        <TabsContent value="timeline" className="mt-6">
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="text-lg">Customer Journey</CardTitle>
              <CardDescription>Complete history from lead to client conversion (including all modifications)</CardDescription>
            </CardHeader>
            <CardContent>
              {realTimeline.length > 0 ? (
                <div className="mt-4 pl-2">
                  {realTimeline.map((item, index) => {
                    // Map type to icon
                    const iconMap = {
                      'LEAD_CREATED': FileText,
                      'DEMO_SCHEDULED': Presentation,
                      'DEMO_COMPLETED': CheckCircle2,
                      'SQD': Target,
                      'DEAL_CREATED': Handshake,
                      'DEAL_WON': Sparkles,
                      'PRICING_REVISED': Edit2,
                      'PLAN_TERMINATED': ExternalLink
                    };
                    const IconComponent = iconMap[item.type] || Clock;
                    
                    return (
                      <TimelineItem
                        key={index}
                        icon={IconComponent}
                        title={item.title}
                        subtitle={item.subtitle}
                        date={formatDateTime(item.timestamp)}
                        status={item.status}
                        color={item.color}
                        details={item.details}
                        isLast={index === realTimeline.length - 1}
                      />
                    );
                  })}
                </div>
              ) : timelineItems.length > 0 ? (
                /* Fallback to old static timeline if API returns empty */
                <div className="mt-4 pl-2">
                  {timelineItems.map((item, index) => (
                    <TimelineItem
                      key={index}
                      icon={item.icon}
                      title={item.title}
                      subtitle={item.subtitle}
                      date={item.date}
                      status={item.status}
                      color={item.color}
                      details={item.details}
                      isLast={index === timelineItems.length - 1}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <Clock className="w-16 h-16 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">No timeline data available</p>
                  <p className="text-sm mt-1">Journey information will appear once data is linked</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Incentives Tab */}
        <TabsContent value="incentives" className="mt-6">
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Award className="w-5 h-5 text-purple-500" />
                Related Incentives
              </CardTitle>
            </CardHeader>
            <CardContent>
              {incentives.length > 0 ? (
                <div className="space-y-3">
                  {incentives.map((inc) => (
                    <div 
                      key={inc.id} 
                      className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                      onClick={() => navigate(`/incentives?highlight=${inc.id}`)}
                    >
                      <div>
                        <p className="font-medium text-slate-800 dark:text-slate-100">{inc.user_name || 'Unknown'}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className={cn(
                            'text-xs',
                            inc.metric_type === 'SQD' && 'bg-orange-100 text-orange-700',
                            inc.metric_type === 'DEAL_CLOSED' && 'bg-blue-100 text-blue-700',
                            inc.metric_type === 'RENEWAL' && 'bg-purple-100 text-purple-700'
                          )}>
                            {inc.metric_type}
                          </Badge>
                          <span className="text-sm text-slate-500 dark:text-slate-400">{inc.period_month}</span>
                          {inc.tier_upgrade_history && (
                            <Badge className="text-xs bg-green-100 text-green-700">
                              +Tier Upgrade
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-emerald-600 dark:text-emerald-400">{formatCurrency(inc.amount_earned)}</p>
                        <Badge variant="outline" className={cn(
                          'text-xs',
                          inc.status === 'PAID' && 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400',
                          inc.status === 'PENDING' && 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400',
                          inc.status === 'APPROVED' && 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400',
                          inc.status === 'PAYABLE' && 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-400'
                        )}>
                          {inc.status}
                        </Badge>
                        <p className="text-xs text-blue-500 mt-1">Click for details →</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <Award className="w-16 h-16 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">No incentives linked to this client</p>
                  <p className="text-sm mt-1">Incentive records will appear here after deal approval</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ClientDetailPage;
